import { useCallback, useMemo, useState } from "react";
import {
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  BiSearchAlt,
  BiPackage,
  BiErrorCircle,
  BiBadgeCheck,
  BiXCircle,
  BiUpload,
  BiNote,
  BiEdit,
  BiLike,
  BiPauseCircle,
  BiCart,
} from "react-icons/bi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { PRODUCT_STATUS_CHANGE } from "../../../actions/admin/customer-actions";
import ProductAssign from "./../../modals/product-assigned/ProductAssign";
import UploadPictureModal from "../../modals/upload-picture/UploadPictureModal";
import AddNotesModal from "../../modals/add-notes/AddNotesModal";
import EditProductIntakeModal from "../../modals/edit-product-intake/EditProductIntakeModal";
import ProductImageGalleryModal from "../../modals/product-image-gallery/ProductImageGalleryModal";
import { resolveProductIntakeApi } from "../../../utils/productIntakeApi";
import {
  PRODUCT_INTAKE_STATUS_ACTIONS,
  isOnlineShoppingIntakeType,
  isProductIntakeLocked,
} from "../../../utils/productIntakeStatus";

const INTAKE_STATUS_ICONS = {
  approved: BiLike,
  pending: BiSearchAlt,
  on_hold: BiPauseCircle,
  shopping_in_progress: BiCart,
  rejected: BiXCircle,
  received_at_the_warehouse: BiPackage,
};

export default function ProductActions({
  row,
  recordList,
  intakeScope = "admin",
}) {
  const navigate = useNavigate();
  const isPersonalShopper = intakeScope === "personalShopper";
  const intakeApi = useMemo(
    () => resolveProductIntakeApi(intakeScope),
    [intakeScope],
  );
  const isOnlineShopping = isOnlineShoppingIntakeType(
    row?.raw_order_type || row?.order_type,
  );
  const currentStatus = String(row?.activeStatus || "").toLowerCase();
  // For online shopping, item_delivered is not locked — all actions remain available
  // For personal_shopper items, shopping_completed is not locked — status actions still available
  const isLocked = isOnlineShopping
    ? isProductIntakeLocked(currentStatus) && currentStatus !== "item_delivered"
    : isProductIntakeLocked(currentStatus) && currentStatus !== "shopping_completed";
  const customerDeclineNote = String(row?.customer_decline_note || "").trim();
  const canViewDeclineNote =
    currentStatus === "rejected" && Boolean(customerDeclineNote);
  const canAddNotes = !isLocked;
  const canAssign =
    !isLocked &&
    !isPersonalShopper &&
    (row?.raw_order_type === "personal_shopper" ||
      row?.raw_order_type === "pick_up");
  const isPersonalShopperItem =
    row?.raw_order_type === "personal_shopper" ||
    row?.order_type === "personal_shopper";
  // Approved personal-shopper rows have no actions — hide empty VIEW menu
  const hasMenuActions = true;

  const [anchorEl, setAnchorEl] = useState(null);
  const [rowData, setRowData] = useState(null);
  const open = Boolean(anchorEl);
  const [productAssignModal, setProductAssignModal] = useState(false);
  const [uploadPictureModal, setUploadPictureModal] = useState(false);
  const [addNotesModal, setAddNotesModal] = useState(false);
  const [notesModalDefaultTab, setNotesModalDefaultTab] = useState(0);
  const [editIntakeModal, setEditIntakeModal] = useState(false);
  const [editRowSnapshot, setEditRowSnapshot] = useState(null);
  const [uploadRowSnapshot, setUploadRowSnapshot] = useState(null);
  const [viewProductModal, setViewProductModal] = useState(false);

  const dispatch = useDispatch();

  const handleClose = () => setAnchorEl(null);

  const changeCustomeStatus = useCallback(
    async (data) => {
      if (isPersonalShopper) {
        try {
          const response = await intakeApi.statusChange(data);
          toast.success(
            response?.data?.data?.message ||
              response?.data?.message ||
              "Status updated successfully",
          );
          recordList?.();
          handleClose();
        } catch (error) {
          toast.error(error?.data?.message || "Failed to update status");
        }
        return;
      }

      dispatch({
        type: PRODUCT_STATUS_CHANGE,
        payload: {
          ...data,
          onSuccess: () => {
            recordList();
            handleClose();
          },
        },
      });
    },
    [dispatch, recordList, isPersonalShopper, intakeApi],
  );

  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const handleUploadSuccess = () => {
    recordList?.();
  };

  const renderIntakeStatusActions = () =>
    PRODUCT_INTAKE_STATUS_ACTIONS.filter((action) => {
      if (
        isPersonalShopper &&
        (action.status === "approved" || action.status === "rejected")
      ) {
        return false;
      }
      // shopping_completed is only for personal shopper scope, not admin
      if (!isPersonalShopper && action.status === "shopping_completed") {
        return false;
      }
      if (action.hideWhenCurrent && currentStatus === action.status)
        return false;
      if (currentStatus === action.status) return false;
      return true;
    }).map((action) => {
      const Icon = INTAKE_STATUS_ICONS[action.status] || BiSearchAlt;
      const isApproveDisabled =
        action.status === "approved" &&
        row?.raw_order_type === "personal_shopper" &&
        row?.activePaymentStatus !== "paid";
      const menuItem = (
        <MenuItem
          key={action.status}
          disabled={isApproveDisabled}
          onClick={() =>
            changeCustomeStatus({
              id: row.id,
              status: action.status,
            })
          }
        >
          <ListItemIcon>
            <Icon size={20} />
          </ListItemIcon>
          <ListItemText primary={action.label} />
        </MenuItem>
      );
      if (isApproveDisabled) {
        return (
          <Tooltip
            key={action.status}
            title="Payment must be paid to approve"
            placement="left"
            arrow
          >
            <span>{menuItem}</span>
          </Tooltip>
        );
      }
      return menuItem;
    });

  return (
    <>
      {hasMenuActions ? (
        <Link
          component="button"
          underline="none"
          color="primary"
          onClick={handleClick}
        >
          VIEW
        </Link>
      ) : (
        <span style={{ color: "#9C9C9C" }}>-</span>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open && hasMenuActions}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 240, borderRadius: 2, mt: 1 },
        }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            const prefix = isPersonalShopper ? "/personal-shopper/items" : "/admin/items";
            navigate(`${prefix}/${row.id}`);
          }}
        >
          <ListItemIcon>
            <BiPackage size={20} />
          </ListItemIcon>
          <ListItemText primary="View Product" />
        </MenuItem>

        {!isLocked && (
          <MenuItem
            onClick={() => {
              handleClose();
              setUploadRowSnapshot(row);
              setUploadPictureModal(true);
            }}
          >
            <ListItemIcon>
              <BiUpload size={20} />
            </ListItemIcon>
            <ListItemText primary="Upload Picture" />
          </MenuItem>
        )}

        {!isLocked && (
          <MenuItem
            onClick={() => {
              handleClose();
              setEditRowSnapshot(row);
              setEditIntakeModal(true);
            }}
          >
            <ListItemIcon>
              <BiEdit size={20} />
            </ListItemIcon>
            <ListItemText primary={isPersonalShopperItem ? "Edit Qty / Weight / Price" : "Edit Qty / Weight"} />
          </MenuItem>
        )}



        {canAddNotes && (
          <MenuItem
            onClick={() => {
              handleClose();
              setNotesModalDefaultTab(0);
              setAddNotesModal(true);
            }}
          >
            <ListItemIcon>
              <BiNote size={20} />
            </ListItemIcon>
            <ListItemText primary="Add Notes" />
          </MenuItem>
        )}

        {canViewDeclineNote && (
          <MenuItem
            onClick={() => {
              handleClose();
              setNotesModalDefaultTab(1);
              setAddNotesModal(true);
            }}
          >
            <ListItemIcon>
              <BiXCircle size={20} />
            </ListItemIcon>
            <ListItemText primary="View Note" />
          </MenuItem>
        )}

        {!isLocked && <Divider />}

        {!isLocked && !isOnlineShopping && (
          <>
            {renderIntakeStatusActions()}
            <Divider />
          </>
        )}

        {isOnlineShopping && !isLocked && (
          <>
            {currentStatus !== "approved" && (
              <MenuItem
                onClick={() =>
                  changeCustomeStatus({ id: row.id, status: "approved" })
                }
              >
                <ListItemIcon>
                  <BiLike size={20} />
                </ListItemIcon>
                <ListItemText primary="Approve" />
              </MenuItem>
            )}

            <MenuItem
              onClick={() =>
                changeCustomeStatus({ id: row.id, status: "pending" })
              }
            >
              <ListItemIcon>
                <BiSearchAlt size={20} />
              </ListItemIcon>
              <ListItemText primary="Pending" />
            </MenuItem>

            {currentStatus === "pending" && (
              <MenuItem
                onClick={() =>
                  changeCustomeStatus({ id: row.id, status: "review" })
                }
              >
                <ListItemIcon>
                  <BiSearchAlt size={20} />
                </ListItemIcon>
                <ListItemText primary="Review" />
              </MenuItem>
            )}

            {currentStatus === "review" && (
              <MenuItem
                onClick={() =>
                  changeCustomeStatus({ id: row.id, status: "wait_to_deliver" })
                }
              >
                <ListItemIcon>
                  <BiPackage size={20} />
                </ListItemIcon>
                <ListItemText primary="Wait To Deliver" />
              </MenuItem>
            )}

            {(currentStatus === "review" ||
              currentStatus === "wait_to_deliver") && (
              <MenuItem
                onClick={() =>
                  changeCustomeStatus({ id: row.id, status: "un_delivered" })
                }
              >
                <ListItemIcon>
                  <BiErrorCircle size={20} />
                </ListItemIcon>
                <ListItemText primary="Un Delivered" />
              </MenuItem>
            )}

            {currentStatus !== "item_delivered" && (
              <MenuItem
                onClick={() =>
                  changeCustomeStatus({ id: row.id, status: "item_delivered" })
                }
              >
                <ListItemIcon>
                  <BiBadgeCheck size={20} />
                </ListItemIcon>
                <ListItemText primary="Item Delivered" />
              </MenuItem>
            )}

            {currentStatus !== "received_at_the_warehouse" && (
              <MenuItem
                onClick={() =>
                  changeCustomeStatus({ id: row.id,
                    status: "received_at_the_warehouse",
                  })
                }
              >
                <ListItemIcon>
                  <BiPackage size={20} />
                </ListItemIcon>
                <ListItemText primary="Received at the warehouse" />
              </MenuItem>
            )}

            <MenuItem
              onClick={() =>
                changeCustomeStatus({ id: row.id, status: "rejected" })
              }
            >
              <ListItemIcon>
                <BiXCircle size={20} />
              </ListItemIcon>
              <ListItemText primary="Rejected" />
            </MenuItem>

            <Divider />
          </>
        )}

        {!isLocked &&
          !isPersonalShopper &&
          (row?.raw_order_type === "personal_shopper" ||
            row?.raw_order_type === "pick_up") && (
            <MenuItem
              onClick={() => {
                setProductAssignModal(true);
                setRowData(row);
              }}
            >
              <ListItemIcon>
                <BiSearchAlt size={20} />
              </ListItemIcon>
              <ListItemText
                primary={row?.isShopperAssigned ? "Re-assign" : "Assign"}
              />
            </MenuItem>
          )}
      </Menu>

      {!isPersonalShopper && productAssignModal && (
        <ProductAssign
          open={productAssignModal}
          handleOpen={productAssignModal}
          rowData={rowData}
          handleClose={() => setProductAssignModal(false)}
          onSaveSuccess={() => {
            setProductAssignModal(false);
            setAnchorEl(null);
          }}
        />
      )}

      <UploadPictureModal
        open={uploadPictureModal}
        handleClose={() => {
          setUploadPictureModal(false);
          setUploadRowSnapshot(null);
        }}
        row={uploadRowSnapshot || row}
        recordList={handleUploadSuccess}
        intakeScope={intakeScope}
      />

      <AddNotesModal
        open={addNotesModal}
        handleClose={() => {
          setAddNotesModal(false);
          setNotesModalDefaultTab(0);
        }}
        row={row}
        recordList={recordList}
        intakeScope={intakeScope}
        defaultTab={notesModalDefaultTab}
        onSaveSuccess={() => {
          setAddNotesModal(false);
          setNotesModalDefaultTab(0);
          setAnchorEl(null);
        }}
      />

      <EditProductIntakeModal
        open={editIntakeModal}
        handleClose={() => {
          setEditIntakeModal(false);
          setEditRowSnapshot(null);
        }}
        row={editRowSnapshot || row}
        recordList={recordList}
        intakeScope={intakeScope}
        isPersonalShopperItem={isPersonalShopperItem}
        onSaveSuccess={() => {
          setEditIntakeModal(false);
          setEditRowSnapshot(null);
          setAnchorEl(null);
        }}
      />

      {!isPersonalShopper && (
        <ProductImageGalleryModal
          open={viewProductModal}
          handleClose={() => setViewProductModal(false)}
          images={row?.product_image || []}
          productName={row?.product_name}
          productId={row?.id}
          invoiceImage={row?.invoice_of_product}
          recordList={recordList}
          intakeScope={intakeScope}
        />
      )}
    </>
  );
}
